'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useStore } from '@/store/store';
import { getConfig } from '@/utils/config/config';
import { MdsClient } from '@/lib/mds/mds-client';
import { ddRumError } from '@/utils/datadog';
import { ddErrorMdsInit } from '@/utils/datadog/site-wide-jackpot';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';
import { createSwjMessageHandler } from './utils/swj-message-handler';
import { checkSiteWideJackpotsUserOptInStatus } from './utils/swj-opt-in-manager';
import { SWJ_MESSAGE_TYPE } from './utils/swj-types';
import type { MessageHandler } from '@/lib/mds/types';
import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';

// Multiplier values for site-wide jackpot opt-in status
// 0 = opted out / no multiplier
// 1 = 1x multiplier (standard opt-in)
// 2 = 2x multiplier (boosted opt-in)
const MULTIPLIER_OPTED_OUT = 0;

/**
 * Hook for initializing and managing the MDS consumer for real-time opt-in status updates.
 */
export const useMdsConsumer = (jackpotData: SiteWideJackpotData) => {
    const jackpotId = jackpotData?.provider_jackpot_id?.[0]?.text || '';
    const enabled = jackpotData.enabled ?? false;
    const config = getConfig();
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const setMultiplier = useStore((state) => state.siteWideJackpot.setMultiplier);
    const setHasSwjInitialized = useStore((state) => state.siteWideJackpot.setHasSwjInitialized);
    const setOptInStatusError = useStore((state) => state.siteWideJackpot.setOptInStatusError);

    const isInitializedRef = useRef(false);
    const isReadInFlightRef = useRef(false);
    const messageHandlerRef = useRef<MessageHandler | null>(null);
    const mdsEndpointRef = useRef<string | null>(null);
    const gthBaseUrlRef = useRef<string | null>(null);

    const readOptInStatus = useCallback(
        async ({ resetOnError }: { resetOnError: boolean }) => {
            const gthBaseUrl = gthBaseUrlRef.current;

            if (!gthBaseUrl || !jackpotId || isReadInFlightRef.current) {
                return;
            }

            isReadInFlightRef.current = true;

            try {
                await checkSiteWideJackpotsUserOptInStatus(gthBaseUrl, jackpotId, {
                    onOptInStatusChange: (value) => setMultiplier(value as 0 | 1 | 2),
                    onHasInitialized: () => setHasSwjInitialized(true),
                    onError: () => {
                        if (resetOnError) {
                            setOptInStatusError(MULTIPLIER_OPTED_OUT);
                        }
                    }
                });
            } finally {
                isReadInFlightRef.current = false;
            }
        },
        [jackpotId, setMultiplier, setHasSwjInitialized, setOptInStatusError]
    );

    useEffect(() => {
        const userId = getCookieValue(COOKIES.USER_ID) as string;
        const authToken = getCookieValue(COOKIES.AUTH_TOKEN) as string;

        // Require both isLoggedIn state AND valid cookies to prevent 401 errors
        if (!enabled || !isLoggedIn || !userId || !authToken || !jackpotId || isInitializedRef.current) {
            return () => {};
        }

        const initializeMds = async () => {
            const region = getCookieValue(COOKIES.REGION) as string;
            const mdsBaseUrl = config?.apis?.MdsClient?.baseUrl;

            if (!mdsBaseUrl) {
                return;
            }

            const mdsEndpoint = mdsBaseUrl.replace('statePlaceholder', region?.toLowerCase() || 'mi');
            mdsEndpointRef.current = mdsEndpoint;

            const gthHost = config?.apis?.SiteWideJackpotClient?.host?.replace(
                'statePlaceholder',
                region?.toLowerCase() || 'mi'
            );
            gthBaseUrlRef.current = gthHost ? `https://${gthHost}` : null;

            try {
                const mdsClient = MdsClient.getInstance(mdsEndpoint);
                messageHandlerRef.current = createSwjMessageHandler(jackpotId, setMultiplier);
                mdsClient.on(SWJ_MESSAGE_TYPE, messageHandlerRef.current);
                mdsClient.initSocket(userId);
                isInitializedRef.current = true;
            } catch (error) {
                ddRumError(ddErrorMdsInit(error as Error, { jackpotId }));
            }

            await readOptInStatus({ resetOnError: true });
        };

        initializeMds();

        return () => {
            if (!isInitializedRef.current) return;

            // Cleanup MDS connection
            if (messageHandlerRef.current && mdsEndpointRef.current) {
                try {
                    const mdsClient = MdsClient.getInstance(mdsEndpointRef.current);
                    mdsClient.off(SWJ_MESSAGE_TYPE, messageHandlerRef.current);
                } catch {
                    // Instance might not exist
                }
                messageHandlerRef.current = null;
            }
            MdsClient.resetInstance();
            mdsEndpointRef.current = null;
            gthBaseUrlRef.current = null;
            isInitializedRef.current = false;

            // Read current login state from store (avoids stale closure)
            const currentlyLoggedIn = useStore.getState().userContext.isLoggedIn;
            if (!currentlyLoggedIn) {
                setMultiplier(MULTIPLIER_OPTED_OUT);
                setHasSwjInitialized(undefined);
            }
        };
    }, [
        enabled,
        isLoggedIn,
        jackpotId,
        config,
        readOptInStatus,
        setMultiplier,
        setHasSwjInitialized,
        setOptInStatusError
    ]);

    useEffect(() => {
        if (!enabled || !isLoggedIn || !jackpotId) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            void readOptInStatus({ resetOnError: false });
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [enabled, isLoggedIn, jackpotId, readOptInStatus]);
};
